import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { dynamicAPIImage } from "../../Utils";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Form, FormGroup, Input, Label } from "reactstrap";
import { useState, useEffect, useRef } from "react";
import {
  H5,
  H2,
  H6,
  Image,
  LI,
  P,
  UL,
  Ribbon,
  Btn,
} from "../../AbstractElements";
import CommonCardHeader from "../CommonComponents/CommonCardHeader/CommonCardHeader";
import { useAppSelector } from "../../ReduxToolkit/Hooks";
import Swal from "sweetalert2";

//MODALS
import CreateActivityModal from "../UiKits/Modal/Applicants/CreateActivityModal";

//TYPE
import { ApplicantActivity } from "../../Types/Applicants/ApplicantsActivity";
import { ViewCandidateProfile } from "../../Types/Applicants/Candidates";

//SERVICES
import {
  APPLICANT_ACTIVITY_QUERY_KEY,
  applicantActivityService,
} from "../../Services/applicantsActivityServices";
import {
  VIEW_CANDIDATES_QUERY_KEY,
  applicantsService,
} from "../../Services/applicantsService";

interface CandidateProfileProps {
  ApplNo: string;
}

const Timeline: React.FC<CandidateProfileProps> = ({ ApplNo }) => {
  const [modal, setModal] = useState(false);
  const toggle = () => setModal((prev) => !prev);
  const user = useAppSelector((state) => state.auth.user);
  const queryClient = useQueryClient();
  const { data: activityData = [], isLoading: TimelineisFetching } = useQuery<
    ApplicantActivity[]
  >({
    queryKey: [...APPLICANT_ACTIVITY_QUERY_KEY, ApplNo],
    queryFn: () => applicantActivityService.GetByApplNo(ApplNo as string),
    select: (data) => data.filter((item) => item.status === 1),
  });

  const { data: candidateprofileData, isLoading: FormisFetching } = useQuery({
    queryKey: [VIEW_CANDIDATES_QUERY_KEY, ApplNo],
    queryFn: () => applicantsService.getCandidateProfile(ApplNo),
  });

  const toggleMutation = useMutation({
    mutationFn: applicantActivityService.toggleStatus,
    onSuccess: async (variables: { id: number; status_label: string }) => {
      await queryClient.invalidateQueries({
        queryKey: [APPLICANT_ACTIVITY_QUERY_KEY],
      });
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: "Remarks has been deleted successfully",
        confirmButtonColor: "#4e73df",
      });
      // setIsRowLoading(false);
    },
    onError: (error: any) => {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Transaction Failed",
        text:
          error.response?.data?.message ||
          "Failed to Delete Remark please reload and try again.",
        confirmButtonColor: "#4e73df",
      });
      // setIsRowLoading(false);
    },
  });

  const handleDelete = (id: number) => {
    Swal.fire({
      icon: "question",
      title: `Delete Activity`,
      html: `Are you sure you want to delete this activity?`,
      showCancelButton: true,

      confirmButtonText: '<i class="fa fa-trash"></i> Delete Remark',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',

      confirmButtonColor: "#4e73df", // Primary
      cancelButtonColor: "#dc3545", // Danger
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting Activity...",
          text: "Please wait while your activity is being deleted.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        toggleMutation.mutate(id);
      }
    });
  };

  const handleNewAction = (applNo: string) => {
    setModal(true);
  };

  return (
    <Col sm="12" className="notification box-col-12">
      <Card className="card-absolute dark-timeline">
        <CommonCardHeader
          headClass="bg-primary"
          tagClass="txt-light"
          icon={<i className="icofont icofont-chart-flow-alt-2 me-1"></i>}
          title={"Activity Timeline"}
        />
        <CardBody>
          <CreateActivityModal isOpen={modal} toggle={toggle} ApplNo={ApplNo} />
          <UL className="simple-list">
            {activityData?.map((item, index) => (
              <LI className="d-flex" key={index}>
                <div className={`activity-dot-${item.activity_color}`} />

                <div className="ms-3">
                  <P className="d-flex justify-content-between mb-2">
                    <span className="date-content light-background">
                      {String(index + 1).padStart(2, "0")}. {item.activity_name}
                    </span>
                  </P>

                  <H6>
                    {item.action_date}
                    <span className="dot-notification" />
                  </H6>

                  <P
                    className="f-light f-w-200 mb-0"
                    style={{
                      fontSize: "12px",
                      lineHeight: 1,
                      letterSpacing: "0.5px",
                    }}
                  >
                    {item.action_by_user && (
                      <>
                        <b>Action By:</b> {item.action_by_user}
                        <br />
                        <br />
                      </>
                    )}
                    {item.description}
                    <br />
                    {item.note && (
                      <>
                        <b>Note:</b> {item.note}
                        <br />
                      </>
                    )}
                  </P>

                  <br />
                </div>
              </LI>
            ))}
            {![8, 9, 10, 11].includes(
              candidateprofileData?.application_status,
            ) && (
              <LI className="d-flex">
                <div className={`activity-dot-warning`} />

                <div className="ms-3">
                  <Btn
                    color="info"
                    size="sm"
                    onClick={() => handleNewAction(ApplNo)}
                  >
                    <i className="icofont icofont-order" />
                    Add Action
                  </Btn>{" "}
                  {activityData.length - 1 >= 1 && (
                    <Btn
                      color="danger"
                      size="sm"
                      onClick={() =>
                        handleDelete(activityData[activityData.length - 1]?.id)
                      }
                    >
                      <i className="icofont icofont-trash" />
                      Delete Previous
                    </Btn>
                  )}
                </div>
              </LI>
            )}
          </UL>
        </CardBody>
      </Card>
    </Col>
  );
};
export default Timeline;
